import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash, Plus, User, UsersThree, Tag, X, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STRAT_LABELS = {
  round_robin: "Round-robin",
  load_based: "Load-based",
  skill_based: "Skill-based",
  manual: "Manual",
};

export default function RoutingRules() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [target, setTarget] = useState("individual");
  const [form, setForm] = useState({ domain: "", client: "", modules: "itsm", assignee_id: "unassigned", group_id: "", group: "", priority_override: "none", sla_plan_id: "none", business_hours_id: "none" });
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [plans, setPlans] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);

  const loadAudit = () => api.get("/routing-audit?limit=200").then(r => setAudit(r.data)).catch(() => {});

  const load = () => api.get("/routing-rules").then(r => setRules(r.data));
  const loadCats = () => api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadCats();
    api.get("/users").then(r => setUsers(r.data.filter(u => u.role !== "end_user")));
    api.get("/assignment-groups").then(r => setGroups(r.data));
    api.get("/sla-plans").then(r => setPlans(r.data)).catch(() => {});
    api.get("/business-calendars").then(r => setCalendars(r.data)).catch(() => {});
  }, []);

  const addCat = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/categories", { name });
      setCategories(data);
      setNewCat("");
      toast.success(`Category "${name}" added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add category"); }
  };
  const removeCat = async (name) => {
    try {
      const { data } = await api.delete(`/categories/${encodeURIComponent(name)}`);
      setCategories(data);
      toast.success(`Category "${name}" removed`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove category"); }
  };

  const submit = async () => {
    if (!form.domain) return toast.error("Domain required");
    if (target === "group" && !form.group_id) return toast.error("Select a group");
    const grp = groups.find(g => g.id === form.group_id);
    const modulesMap = { itsm: ["itsm"], soc: ["soc"], both: ["itsm", "soc"] };
    const payload = {
      domain: form.domain.toLowerCase(),
      client: (form.client || "").trim(),
      modules: modulesMap[form.modules] || ["itsm"],
      assignee_id: target === "individual" && form.assignee_id !== "unassigned" ? form.assignee_id : null,
      group_id: target === "group" ? form.group_id : null,
      group: target === "group" ? (grp?.name || null) : (form.group || null),
      priority_override: form.priority_override === "none" ? null : form.priority_override,
      sla_plan_id: form.sla_plan_id === "none" ? null : form.sla_plan_id,
      business_hours_id: form.business_hours_id === "none" ? null : form.business_hours_id,
    };
    try {
      await api.post("/routing-rules", payload);
      toast.success("Rule added");
      setForm({ domain: "", client: "", modules: "itsm", assignee_id: "unassigned", group_id: "", group: "", priority_override: "none", sla_plan_id: "none", business_hours_id: "none" });
      setTarget("individual");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { await api.delete(`/routing-rules/${id}`); toast.success("Rule removed"); load(); };

  const MOD_MAP = { itsm: ["itsm"], soc: ["soc"], both: ["itsm", "soc"] };
  const modulesValue = (m) => { const s = new Set(m && m.length ? m : ["itsm"]); return s.has("itsm") && s.has("soc") ? "both" : s.has("soc") ? "soc" : "itsm"; };
  const patchRule = async (rule, updates, msg = "Rule updated") => {
    try {
      const { data } = await api.patch(`/routing-rules/${rule.id}`, updates);
      const n = data?.recalculated_tickets || 0;
      toast.success(n > 0 ? `${msg} · SLAs refreshed on ${n} open ticket(s)` : msg);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };
  const setModules = (rule, value) => patchRule(rule, { modules: MOD_MAP[value] }, "Module availability updated");
  const deleteClient = async (name) => {
    if (!name) return;
    if (!window.confirm(`Delete client "${name}"?\n\nThis removes it from the client registry and clears it from any routing rules that use it. (Assets saved under this client are not deleted.)`)) return;
    try {
      const { data } = await api.delete(`/clients/${encodeURIComponent(name)}`);
      toast.success(`Client "${name}" deleted — ${data.rules_cleared} rule(s) cleared`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete client"); }
  };
  const targetValue = (r) => (r.group_id ? `group:${r.group_id}` : r.assignee_id ? `user:${r.assignee_id}` : "none");
  const setTargetVal = (rule, val) => {
    if (val === "none") return patchRule(rule, { assignee_id: null, group_id: null }, "Target cleared");
    const [kind, id] = val.split(":");
    if (kind === "user") return patchRule(rule, { assignee_id: id, group_id: null }, "Assignee updated");
    const g = groups.find(x => x.id === id);
    return patchRule(rule, { group_id: id, assignee_id: null, group: g?.name || null }, "Group updated");
  };

  return (
    <div className="p-6" data-testid="routing-view">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Automation</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Email-Domain Routing</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Auto-assign incoming tickets by the requester's email domain. Route to a single <span className="text-primary">individual</span> or an <span className="text-primary">assignment group</span> (resolved by its strategy). Use <span className="font-mono text-primary">*</span> as a catch-all.</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={auditOpen} onOpenChange={(o) => { setAuditOpen(o); if (o) loadAudit(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="routing-audit-btn" className="rounded-sm font-mono text-xs uppercase tracking-widest shrink-0"><ClockCounterClockwise size={15} className="mr-2" />Change History</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="routing-audit-dialog">
              <DialogHeader><DialogTitle className="font-display">Routing Rule Change History</DialogTitle></DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
                {audit.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">No changes recorded yet.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {audit.map((a) => {
                      const tag = a.action === "created" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                        : a.action === "deleted" ? "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30"
                        : a.action === "client_deleted" ? "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30"
                        : a.action === "sla_recompute" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
                        : "bg-primary/15 text-primary border-primary/30";
                      const label = a.action === "sla_recompute" ? "SLA recalc" : a.action === "client_deleted" ? "client del" : a.action;
                      return (
                        <div key={a.id} className="py-3 flex items-start gap-3" data-testid={`routing-audit-row-${a.id}`}>
                          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border shrink-0 ${tag}`}>{label}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm"><span className="font-mono text-primary">{a.domain}</span>{a.client ? <span className="text-muted-foreground"> · {a.client}</span> : null}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 break-words">{a.detail}</div>
                            <div className="text-[10px] font-mono text-muted-foreground mt-1">{a.changed_by_name || "—"} · {new Date(a.changed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4 border-border rounded-sm mb-6">
        <div className="mb-4">
          <Label className="text-xs uppercase">Assignment Target</Label>
          <Tabs value={target} onValueChange={setTarget} className="mt-1">
            <TabsList className="rounded-sm">
              <TabsTrigger data-testid="target-individual" value="individual" className="rounded-sm font-mono text-xs uppercase"><User size={13} className="mr-1.5" />Individual</TabsTrigger>
              <TabsTrigger data-testid="target-group" value="group" className="rounded-sm font-mono text-xs uppercase"><UsersThree size={13} className="mr-1.5" />Group</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><Label className="text-xs uppercase">Domain</Label><Input data-testid="rule-domain" className="mt-1 rounded-sm font-mono" placeholder="acme.com or *" value={form.domain} onChange={(e)=>setForm({...form,domain:e.target.value})} /></div>
          <div><Label className="text-xs uppercase">Client</Label><Input data-testid="rule-client" className="mt-1 rounded-sm" placeholder="e.g. Acme Corp" value={form.client} onChange={(e)=>setForm({...form,client:e.target.value})} /></div>
          <div><Label className="text-xs uppercase">Module Availability</Label>
            <Select value={form.modules} onValueChange={(v)=>setForm({...form,modules:v})}>
              <SelectTrigger data-testid="rule-modules" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="itsm">ITSM only</SelectItem>
                <SelectItem value="soc">SOC only</SelectItem>
                <SelectItem value="both">Both (ITSM + SOC)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {target === "individual" ? (
            <>
              <div><Label className="text-xs uppercase">Assign to</Label>
                <Select value={form.assignee_id} onValueChange={(v)=>setForm({...form,assignee_id:v})}>
                  <SelectTrigger data-testid="rule-assignee" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">— none —</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase">Group tag</Label><Input data-testid="rule-group" className="mt-1 rounded-sm" placeholder="e.g. Enterprise" value={form.group} onChange={(e)=>setForm({...form,group:e.target.value})} /></div>
            </>
          ) : (
            <div className="md:col-span-2"><Label className="text-xs uppercase">Assignment Group</Label>
              <Select value={form.group_id} onValueChange={(v)=>setForm({...form,group_id:v})}>
                <SelectTrigger data-testid="rule-group-select" className="mt-1 rounded-sm"><SelectValue placeholder="Select a group…" /></SelectTrigger>
                <SelectContent>
                  {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name} · {STRAT_LABELS[g.strategy] || g.strategy}</SelectItem>)}
                  {groups.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No groups — create one under Assignment Groups.</div>}
                </SelectContent>
              </Select>
            </div>
          )}

          <div><Label className="text-xs uppercase">Priority override</Label>
            <Select value={form.priority_override} onValueChange={(v)=>setForm({...form,priority_override:v})}>
              <SelectTrigger data-testid="rule-priority" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— none —</SelectItem>{["P1","P2","P3","P4"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">SLA Plan</Label>
            <Select value={form.sla_plan_id} onValueChange={(v)=>setForm({...form,sla_plan_id:v})}>
              <SelectTrigger data-testid="rule-sla-plan" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Default</SelectItem>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Business Hours</Label>
            <Select value={form.business_hours_id} onValueChange={(v)=>setForm({...form,business_hours_id:v})}>
              <SelectTrigger data-testid="rule-business-hours" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Default</SelectItem>{calendars.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="rule-add" onClick={submit} className="rounded-sm font-mono uppercase text-xs h-10"><Plus size={14} className="mr-2"/>Add</Button>
        </div>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
            <th className="text-left px-4 py-3">Domain</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Modules</th><th className="text-left px-4 py-3">Target</th><th className="text-left px-4 py-3">Group</th><th className="text-left px-4 py-3">Priority Override</th><th className="text-left px-4 py-3">SLA Plan</th><th className="text-left px-4 py-3">Business Hours</th><th></th>
          </tr></thead>
          <tbody>
            {rules.map(r => {
              return (
                <tr key={r.id} className="border-b border-border/60" data-testid={`rule-row-${r.domain}`}>
                  <td className="px-4 py-3 font-mono text-primary">{r.domain}</td>
                  <td className="px-4 py-3">
                    {r.client ? (
                      <span className="inline-flex items-center gap-2">
                        <span>{r.client}</span>
                        {(user?.role === "admin" || user?.role === "manager") && (
                          <button data-testid={`rule-client-delete-${r.domain}`} onClick={() => deleteClient(r.client)} title={`Delete client "${r.client}"`}
                            className="text-red-500 hover:text-red-600 transition-colors"><Trash size={15} /></button>
                        )}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={modulesValue(r.modules)} onValueChange={(v) => setModules(r, v)}>
                      <SelectTrigger data-testid={`rule-modules-${r.domain}`} className="h-7 w-28 rounded-sm text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="itsm">ITSM</SelectItem>
                        <SelectItem value="soc">SOC</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={targetValue(r)} onValueChange={(v) => setTargetVal(r, v)}>
                      <SelectTrigger data-testid={`rule-target-${r.domain}`} className="h-7 w-44 rounded-sm text-xs"><SelectValue placeholder="— none —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— none —</SelectItem>
                        {users.length > 0 && (
                          <SelectGroup>
                            <SelectLabel className="text-[10px] uppercase">Individuals</SelectLabel>
                            {users.map(x => <SelectItem key={`u-${x.id}`} value={`user:${x.id}`}>{x.name}</SelectItem>)}
                          </SelectGroup>
                        )}
                        {groups.length > 0 && <SelectSeparator />}
                        {groups.length > 0 && (
                          <SelectGroup>
                            <SelectLabel className="text-[10px] uppercase">Groups</SelectLabel>
                            {groups.map(x => <SelectItem key={`g-${x.id}`} value={`group:${x.id}`}>{x.name} · {STRAT_LABELS[x.strategy] || x.strategy}</SelectItem>)}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{r.group || "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={r.priority_override || "none"} onValueChange={(v) => patchRule(r, { priority_override: v === "none" ? null : v }, "Priority updated")}>
                      <SelectTrigger data-testid={`rule-priority-${r.domain}`} className="h-7 w-24 rounded-sm text-xs font-mono"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">— none —</SelectItem>{["P1","P2","P3","P4"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={r.sla_plan_id || "none"} onValueChange={(v) => patchRule(r, { sla_plan_id: v === "none" ? null : v }, "SLA plan updated")}>
                      <SelectTrigger data-testid={`rule-sla-${r.domain}`} className="h-7 w-36 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Default</SelectItem>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={r.business_hours_id || "none"} onValueChange={(v) => patchRule(r, { business_hours_id: v === "none" ? null : v }, "Business hours updated")}>
                      <SelectTrigger data-testid={`rule-bh-${r.domain}`} className="h-7 w-36 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Default</SelectItem>{calendars.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right"><button data-testid={`rule-del-${r.domain}`} onClick={()=>remove(r.id)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><Trash size={14} /></button></td>
                </tr>
              );
            })}
            {rules.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground text-sm">No routing rules yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      {user?.role === "admin" && (
        <Card className="p-5 border-border rounded-sm mt-8" data-testid="categories-card">
          <div className="flex items-center gap-2 mb-1">
            <Tag size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Ticket Categories</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Manage the categories agents and end-users can pick when logging a ticket. Changes apply everywhere immediately (New Incident, End-User Portal, Report Builder).</p>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="category-chips">
            {categories.map(c => (
              <span key={c} data-testid={`category-chip-${c}`} className="inline-flex items-center gap-1.5 text-xs font-mono bg-accent/30 border border-border rounded-sm px-2.5 py-1.5">
                {c}
                <button data-testid={`category-remove-${c}`} onClick={() => removeCat(c)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><X size={12} /></button>
              </span>
            ))}
            {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories yet.</span>}
          </div>
          <div className="flex items-end gap-2 max-w-md">
            <div className="flex-1">
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Add category</Label>
              <Input data-testid="category-input" className="mt-1 rounded-sm" placeholder="e.g. Telephony" value={newCat}
                onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addCat(); }} />
            </div>
            <Button data-testid="category-add" onClick={addCat} className="rounded-sm font-mono uppercase text-xs h-10"><Plus size={14} className="mr-2" />Add</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
